// WhatsApp-style delivery/read ticks and message reactions for group rooms —
// the pure derivation half. Firestore writes live in services/roomActions.ts.
//
// Receipts are stored on the ROOM doc as two `userId → timestamp` maps
// (`deliveredAt`, `readAt`, see types.ChatRoom) rather than per-message
// arrays: a member who opens a room with 200 unread messages then costs one
// write, not 200, and every message's tick state falls out of a comparison.
//
// A member's read mark implies delivery — a message can't be on screen
// without having arrived — so `readAt` counts toward delivered even if a
// `deliveredAt` write was missed (e.g. the reader was offline when the
// message landed and opened the room straight from a cold start).
//
// MOBILE PARITY: the WolfDriver app shares this `rooms` collection but does
// not write these maps yet. Until it does, a member who only uses mobile
// never advances anyone's ticks — their teammate's message stays at ✓. The
// field names above are the contract to implement there (same doc, same keys).

import { ChatRoom, RoomMessage } from '../types';

/** `sent` = written to Firestore · `delivered` = every other member's session
 *  has it (✓✓ grey) · `read` = every other member has seen it (✓✓ blue). */
export type TickState = 'sent' | 'delivered' | 'read';

const stampFor = (map: Record<string, number> | undefined, userId: string): number =>
  map?.[userId] ?? 0;

/** Members who need to receive/read a message for it to advance a tick — i.e.
 *  everyone in the room except its sender. */
export const receiptAudience = (
  room: Pick<ChatRoom, 'memberIds'>,
  senderId: string,
): string[] => room.memberIds.filter((id) => id !== senderId);

const deliveredStamp = (room: Pick<ChatRoom, 'deliveredAt' | 'readAt'>, userId: string): number =>
  Math.max(stampFor(room.deliveredAt, userId), stampFor(room.readAt, userId));

/**
 * Tick state for one message. Only meaningful on your OWN messages — that's
 * the only place WhatsApp shows ticks, and the only place the room's receipt
 * maps describe "everyone else".
 *
 * A one-member room (everybody else left) stays at `sent`: there is nobody
 * left to deliver to, and claiming blue ticks would be a lie.
 */
export const messageTickState = (
  msg: Pick<RoomMessage, 'senderId' | 'timestamp'>,
  room: Pick<ChatRoom, 'memberIds' | 'deliveredAt' | 'readAt'>,
): TickState => {
  const audience = receiptAudience(room, msg.senderId);
  if (audience.length === 0) return 'sent';
  if (audience.every((id) => stampFor(room.readAt, id) >= msg.timestamp)) return 'read';
  if (audience.every((id) => deliveredStamp(room, id) >= msg.timestamp)) return 'delivered';
  return 'sent';
};

/** Per-member breakdown behind the ticks — powers the hover tooltip ("Read by
 *  2 of 3"). Sender excluded, as in `receiptAudience`. */
export const receiptBreakdown = (
  msg: Pick<RoomMessage, 'senderId' | 'timestamp'>,
  room: Pick<ChatRoom, 'memberIds' | 'deliveredAt' | 'readAt'>,
): { readIds: string[]; deliveredIds: string[]; pendingIds: string[] } => {
  const audience = receiptAudience(room, msg.senderId);
  const readIds = audience.filter((id) => stampFor(room.readAt, id) >= msg.timestamp);
  const deliveredIds = audience.filter(
    (id) => !readIds.includes(id) && deliveredStamp(room, id) >= msg.timestamp
  );
  const pendingIds = audience.filter(
    (id) => !readIds.includes(id) && !deliveredIds.includes(id)
  );
  return { readIds, deliveredIds, pendingIds };
};

// ── Reactions ───────────────────────────────────────────────────────────────

/** The quick-pick row at the top of the picker, in WhatsApp's order. */
export const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;

/**
 * The full picker catalogue, grouped for the scrollable grid under the quick
 * row. Deliberately hand-picked rather than an emoji-library dependency: this
 * is a dispatch tool, so the last group is the vocabulary the floor actually
 * uses (packed, out for delivery, cash, address, running late).
 *
 * An emoji may appear in more than one group (👍 is both quick-pick and a
 * gesture) — render keys must include the group name.
 */
export const EMOJI_GROUPS: ReadonlyArray<{ label: string; emojis: readonly string[] }> = [
  {
    label: 'Smileys',
    emojis: [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
      '🙂', '😊', '😇', '😉', '😍', '🥰', '😘', '😋',
      '😎', '🤩', '🥳', '🤗', '🤔', '🤨', '😐', '😴',
      '😮', '😯', '😳', '🥺', '😢', '😭', '😤', '😡',
      '🤯', '😱', '🤒', '🤝', '🫡', '🤦', '🤷', '👀',
    ],
  },
  {
    label: 'Gestures',
    emojis: ['👍', '👎', '👌', '✌️', '🤞', '🙌', '👏', '🙏', '💪', '👋', '🤙', '☝️'],
  },
  {
    label: 'Hearts & symbols',
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '💯',
      '🔥', '⭐', '✨', '⚡', '✅', '❌', '⚠️', '❓',
    ],
  },
  {
    label: 'On the job',
    emojis: [
      '📦', '🚚', '🏪', '💵', '💳', '📍', '🗺️', '📞',
      '⏰', '🕐', '📝', '🧊', '🎁', '🚨', '🅿️', '🎉',
    ],
  },
];

export interface ReactionSummary {
  emoji: string;
  userIds: string[];
  count: number;
  /** True when the viewer is one of the reactors. */
  mine: boolean;
}

/**
 * Collapse a message's `reactions` map into render-ready pills, most-reacted
 * first (ties keep the map's insertion order, which is the order the emojis
 * were first used). Empty arrays are dropped — `setMessageReaction` deletes
 * emptied keys, but a doc written by an older client (or a lost race) can
 * still carry one, and a `0` pill is nonsense.
 */
export const summarizeReactions = (
  reactions: Record<string, string[]> | undefined,
  viewerId: string | undefined,
): ReactionSummary[] =>
  Object.entries(reactions ?? {})
    .filter(([, ids]) => ids && ids.length > 0)
    .map(([emoji, ids]) => ({
      emoji,
      userIds: ids,
      count: ids.length,
      mine: !!viewerId && ids.includes(viewerId),
    }))
    .sort((a, b) => b.count - a.count);

/** The single emoji this user has on the message, if any (WhatsApp allows
 *  exactly one). */
export const myReaction = (
  reactions: Record<string, string[]> | undefined,
  userId: string | undefined,
): string | null => {
  if (!userId) return null;
  for (const [emoji, ids] of Object.entries(reactions ?? {})) {
    if (ids?.includes(userId)) return emoji;
  }
  return null;
};
